import type {
  SavedPaymentMethod,
  CustomerBatchHistory,
  CustomerSearchHistory,
  AccountStats,
  SingleSearchResult,
  SingleSearchRequest,
  ProcessingType,
  JobStatus
} from '@/types';
import { logger } from '@/utils/logger';

/**
 * Mock Customer API Service
 * In production, this would call real backend APIs
 */
export class CustomerAPIService {
  private mockPaymentMethods: Map<string, SavedPaymentMethod[]> = new Map();
  private mockBatchHistory: Map<string, CustomerBatchHistory[]> = new Map();
  private mockSearchHistory: Map<string, CustomerSearchHistory[]> = new Map();

  constructor() {
    // Initialize with some mock data
    this.initializeMockData();
  }

  /**
   * Get customer account stats
   */
  async getAccountStats(userId: string): Promise<AccountStats> {
    logger.info('Fetching account stats', { userId });

    const { accountServiceAPI } = await import('./accountServiceAPI');

    try {
      const [batches, searches] = await Promise.all([
        accountServiceAPI.getBatchHistory(userId, 1000),
        accountServiceAPI.getSearchHistory(userId, 1000),
      ]);

      const totalBatchRows = batches.reduce((sum: number, batch) => sum + (Number(batch.successfulRecords) || 0), 0);
      const totalSearchRows = searches.reduce((sum: number, search) => (search.hasResults !== false ? sum + 1 : sum), 0);
      const totalRowsProcessed = totalBatchRows + totalSearchRows;

      const totalBatchCost = batches.reduce((sum: number, batch) => sum + (Number(batch.cost) || 0), 0);
      const totalSearchCost = searches.reduce((sum: number, search) => sum + (Number(search.cost) || 0), 0);

      // Get earliest timestamp for activeSince
      const allTimestamps = [
        ...batches.map((b) => b.timestamp as string),
        ...searches.map((s) => s.timestamp as string)
      ].filter(Boolean).sort();
      const activeSince = allTimestamps[0] || new Date().toISOString();

      return {
        totalBatchesProcessed: batches.length,
        totalSearchesPerformed: searches.length,
        totalSpent: totalBatchCost + totalSearchCost,
        totalRowsProcessed,
        activeSince,
      };
    } catch (error) {
      logger.error('Failed to fetch account stats from backend, returning zeros', error);
      // Return zeros on error rather than throwing
      return {
        totalBatchesProcessed: 0,
        totalSearchesPerformed: 0,
        totalSpent: 0,
        totalRowsProcessed: 0,
        activeSince: new Date().toISOString(),
      };
    }
  }

  /**
   * Get saved payment methods from Stripe
   */
  async getPaymentMethods(userId: string): Promise<SavedPaymentMethod[]> {
    logger.info('Fetching payment methods from Stripe', { userId });

    // Import stripeAPI dynamically to avoid circular dependencies
    const { stripeAPI } = await import('./stripeAPI');
    const { cognitoAuth } = await import('./cognitoAuth');

    try {
      // Get current user's email (needed for Stripe customer lookup)
      const currentUser = cognitoAuth.getCurrentUser();
      if (!currentUser || !currentUser.email) {
        logger.warn('Cannot fetch payment methods: no authenticated user email');
        return [];
      }

      // Fetch payment methods from Stripe Lambda
      const stripeMethods = await stripeAPI.listPaymentMethods(currentUser.email);

      // Transform Stripe response to SavedPaymentMethod format
      const paymentMethods: SavedPaymentMethod[] = (stripeMethods as unknown[]).map((item) => {
        const method = item as Record<string, unknown>;
        return {
          id: (method.id as string) ?? (method.paymentMethodId as string) ?? '',
          userId: userId,
          stripePaymentMethodId: (method.id as string) ?? (method.paymentMethodId as string) ?? '',
          brand: (method.brand as string) ?? ((method.card as Record<string, unknown>)?.brand as string) ?? 'card',
          last4: (method.last4 as string) ?? ((method.card as Record<string, unknown>)?.last4 as string) ?? '0000',
          expiryMonth: (method.exp_month as number) ?? ((method.card as Record<string, unknown>)?.exp_month as number) ?? 12,
          expiryYear: (method.exp_year as number) ?? ((method.card as Record<string, unknown>)?.exp_year as number) ?? 2099,
          isDefault: (method.isDefault as boolean) ?? (method.is_default as boolean) ?? false,
          createdAt: method.created ? new Date((method.created as number) * 1000).toISOString() : new Date().toISOString(),
        };
      });

      logger.info('Payment methods fetched successfully', { count: paymentMethods.length });
      return paymentMethods;
    } catch (error) {
      logger.error('Failed to fetch payment methods from Stripe', error);
      return [];
    }
  }

  /**
   * Add a new payment method
   */
  async addPaymentMethod(
    userId: string,
    stripePaymentMethodId: string,
    brand: string,
    last4: string,
    expiryMonth: number,
    expiryYear: number,
    isDefault: boolean = false
  ): Promise<SavedPaymentMethod> {
    logger.info('Adding payment method', { userId, last4 });

    await this.simulateDelay(400);

    const existingMethods = this.mockPaymentMethods.get(userId) || [];

    // If setting as default, unset others
    if (isDefault) {
      existingMethods.forEach(method => {
        method.isDefault = false;
      });
    }

    const newMethod: SavedPaymentMethod = {
      id: `pm_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      userId,
      stripePaymentMethodId,
      brand,
      last4,
      expiryMonth,
      expiryYear,
      isDefault: isDefault || existingMethods.length === 0,
      createdAt: new Date().toISOString(),
    };

    this.mockPaymentMethods.set(userId, [...existingMethods, newMethod]);

    logger.info('Payment method added', { paymentMethodId: newMethod.id });
    return newMethod;
  }

  /**
   * Remove a payment method
   */
  async removePaymentMethod(userId: string, paymentMethodId: string): Promise<void> {
    logger.info('Removing payment method', { userId, paymentMethodId });

    await this.simulateDelay(300);

    const methods = this.mockPaymentMethods.get(userId) || [];
    const filteredMethods = methods.filter(m => m.id !== paymentMethodId);

    // If we removed the default and there are others, make the first one default
    const removedMethod = methods.find(m => m.id === paymentMethodId);
    if (removedMethod?.isDefault && filteredMethods.length > 0) {
      filteredMethods[0].isDefault = true;
    }

    this.mockPaymentMethods.set(userId, filteredMethods);
    logger.info('Payment method removed', { paymentMethodId });
  }

  /**
   * Set a payment method as default
   */
  async setDefaultPaymentMethod(userId: string, paymentMethodId: string): Promise<void> {
    logger.info('Setting default payment method', { userId, paymentMethodId });

    await this.simulateDelay(200);

    const methods = this.mockPaymentMethods.get(userId) || [];
    methods.forEach(method => {
      method.isDefault = method.id === paymentMethodId;
    });

    this.mockPaymentMethods.set(userId, methods);
    logger.info('Default payment method updated', { paymentMethodId });
  }

  /**
   * Get batch history
   */
  async getBatchHistory(userId: string, limit: number = 50): Promise<CustomerBatchHistory[]> {
    logger.info('Fetching batch history', { userId, limit });

    const { accountServiceAPI } = await import('./accountServiceAPI');

    try {
      const response = await accountServiceAPI.getBatchHistory(userId, limit);

      // Transform backend response to match frontend types
      // Backend uses: filename, processingType, totalRecords, successfulRecords, failedRecords, timestamp
      // Frontend expects: fileName, processingType, rowCount, successfulRows, failedRows, uploadedAt
      const transformedBatches: CustomerBatchHistory[] = response.map((batch: Record<string, unknown>) => ({
        id: (batch.searchId as string) ?? (batch.jobId as string) ?? (batch.id as string) ?? '', // Use searchId as the primary ID from DynamoDB
        userId: (batch.cognitoUserId as string) ?? (batch.userId as string) ?? '',
        jobId: (batch.jobId as string) ?? '',
        fileName: (batch.filename as string) ?? (batch.fileName as string) ?? '',
        processingType: (batch.processingType as ProcessingType) ?? 'validate',
        rowCount: (batch.totalRecords as number) ?? (batch.rowCount as number) ?? 0,
        successfulRows: (batch.successfulRecords as number) ?? (batch.successfulRows as number) ?? 0,
        failedRows: (batch.failedRecords as number) ?? (batch.failedRows as number) ?? 0,
        status: (batch.status as JobStatus) ?? 'completed',
        cost: (batch.cost as number) ?? 0,
        uploadedAt: (batch.timestamp as string) ?? (batch.uploadedAt as string) ?? (batch.createdAt as string) ?? '',
        completedAt: batch.completedAt as string | undefined,
        downloadUrl: batch.downloadUrl as string | undefined,
        expiresAt: batch.expiresAt as string | undefined,
      }));

      logger.info('Batch history transformed', { count: transformedBatches.length });
      return transformedBatches;
    } catch (error) {
      logger.error('Failed to fetch batch history from backend, falling back to empty', error);
      return [];
    }
  }

  /**
   * Save a completed batch to history
   */
  async saveBatchToHistory(
    userId: string,
    jobId: string,
    fileName: string,
    processingType: ProcessingType,
    rowCount: number,
    successfulRows: number,
    failedRows: number,
    status: JobStatus,
    cost: number,
    uploadedAt: string,
    completedAt?: string,
    downloadUrl?: string,
    expiresAt?: string
  ): Promise<void> {
    logger.info('Saving batch to history', { userId, jobId });

    await this.simulateDelay(200);

    const batches = this.mockBatchHistory.get(userId) || [];
    const newBatch: CustomerBatchHistory = {
      id: `batch_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      userId,
      jobId,
      fileName,
      processingType,
      rowCount,
      successfulRows,
      failedRows,
      status,
      cost,
      uploadedAt,
      completedAt,
      downloadUrl,
      expiresAt,
    };

    this.mockBatchHistory.set(userId, [newBatch, ...batches]);
    logger.info('Batch saved to history', { batchId: newBatch.id });
  }

  /**
   * Get search history
   */
  async getSearchHistory(userId: string, limit: number = 50): Promise<CustomerSearchHistory[]> {
    logger.info('Fetching search history', { userId, limit });

    // Import accountServiceAPI dynamically to avoid circular dependencies
    const { accountServiceAPI } = await import('./accountServiceAPI');

    try {
      const response = await accountServiceAPI.getSearchHistory(userId, limit);
      return response as unknown as CustomerSearchHistory[];
    } catch (error) {
      logger.error('Failed to fetch search history from backend, falling back to empty', error);
      return [];
    }
  }

  /**
   * Save a completed search to history
   */
  async saveSearchToHistory(
    userId: string,
    searchId: string,
    searchResult: SingleSearchResult,
    cost: number
  ): Promise<void> {
    logger.info('Saving search to history', { userId, searchId });

    await this.simulateDelay(200);

    const searches = this.mockSearchHistory.get(userId) || [];
    const newSearch: CustomerSearchHistory = {
      id: `search_hist_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      userId,
      searchId,
      searchRequest: searchResult.searchRequest,
      searchType: searchResult.searchRequest.searchType,
      cost,
      timestamp: searchResult.timestamp,
      hasResults: true,
      result: searchResult,
    };

    this.mockSearchHistory.set(userId, [newSearch, ...searches]);
    logger.info('Search saved to history', { searchHistoryId: newSearch.id });
  }

  /**
   * Save a no-hit search to history
   */
  async saveNoHitSearchToHistory(
    userId: string,
    searchRequest: Record<string, unknown>,
    cost: number = 0
  ): Promise<void> {
    logger.info('Saving no-hit search to history', { userId });

    await this.simulateDelay(200);

    const searches = this.mockSearchHistory.get(userId) || [];
    const newSearch: CustomerSearchHistory = {
      id: `search_hist_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      userId,
      searchId: `no_hit_${Date.now()}`,
      searchRequest: searchRequest as unknown as SingleSearchRequest,
      searchType: (searchRequest.searchType as 'scoring' | 'scoring_enrich') ?? 'scoring',
      cost,
      timestamp: new Date().toISOString(),
      hasResults: false,
    };

    this.mockSearchHistory.set(userId, [newSearch, ...searches]);
    logger.info('No-hit search saved to history', { searchHistoryId: newSearch.id });
  }

  /**
   * Get a specific search from history
   */
  async getSearchFromHistory(userId: string, searchHistoryId: string): Promise<CustomerSearchHistory | null> {
    logger.info('Fetching search from history', { userId, searchHistoryId });

    await this.simulateDelay(200);

    const searches = this.mockSearchHistory.get(userId) || [];
    return searches.find(s => s.id === searchHistoryId) || null;
  }

  /**
   * Initialize mock data for development
   */
  private initializeMockData(): void {
    // Mock payment methods for a test user
    const testUserId = 'test_user_123';

    this.mockPaymentMethods.set(testUserId, [
      {
        id: 'pm_1',
        userId: testUserId,
        stripePaymentMethodId: 'pm_test_visa',
        brand: 'visa',
        last4: '4242',
        expiryMonth: 12,
        expiryYear: 2025,
        isDefault: true,
        createdAt: '2024-01-15T10:00:00Z',
      },
      {
        id: 'pm_2',
        userId: testUserId,
        stripePaymentMethodId: 'pm_test_mastercard',
        brand: 'mastercard',
        last4: '5555',
        expiryMonth: 6,
        expiryYear: 2026,
        isDefault: false,
        createdAt: '2024-02-01T14:30:00Z',
      },
    ]);

    // Mock batch history
    this.mockBatchHistory.set(testUserId, [
      {
        id: 'batch_1',
        userId: testUserId,
        jobId: 'job_12345',
        fileName: 'customer_accounts_march.csv',
        processingType: 'scoring_enrich',
        rowCount: 150,
        successfulRows: 148,
        failedRows: 2,
        status: 'completed',
        cost: 142.50,
        uploadedAt: '2024-03-15T09:30:00Z',
        completedAt: '2024-03-15T09:35:00Z',
        downloadUrl: 'https://example.com/results/job_12345.csv',
        expiresAt: '2024-03-22T09:35:00Z',
      },
      {
        id: 'batch_2',
        userId: testUserId,
        jobId: 'job_67890',
        fileName: 'debtors_list_feb.csv',
        processingType: 'scoring',
        rowCount: 75,
        successfulRows: 73,
        failedRows: 2,
        status: 'completed',
        cost: 33.75,
        uploadedAt: '2024-02-28T14:20:00Z',
        completedAt: '2024-02-28T14:23:00Z',
        downloadUrl: 'https://example.com/results/job_67890.csv',
        expiresAt: '2024-03-06T14:23:00Z',
      },
    ]);

    // Mock search history
    this.mockSearchHistory.set(testUserId, [
      {
        id: 'search_1',
        userId: testUserId,
        searchId: 'search_abc123',
        searchRequest: {
          firstName: 'John',
          lastName: 'Smith',
          amountToCollect: 2500,
          searchType: 'scoring_enrich',
          address: '123 Main St',
          city: 'New York',
          state: 'NY',
          zip: '10001',
        },
        searchType: 'scoring_enrich',
        cost: 14.95,
        timestamp: '2024-03-10T11:45:00Z',
        hasResults: true,
      },
      {
        id: 'search_2',
        userId: testUserId,
        searchId: 'search_def456',
        searchRequest: {
          firstName: 'Jane',
          lastName: 'Doe',
          amountToCollect: 1200,
          searchType: 'scoring',
        },
        searchType: 'scoring',
        cost: 9.95,
        timestamp: '2024-03-08T16:20:00Z',
        hasResults: true,
      },
    ]);
  }

  /**
   * Simulate network delay
   */
  private async simulateDelay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const customerAPI = new CustomerAPIService();
